import React, { useEffect, useId, useRef, useState } from "react";

import Grid from "@mui/material/Grid";
import { Response } from "@src/components/ai-elements/response";

import { useReportTextReveal } from "./useReportTextReveal";

interface TextProps {
  text: string;
  doubleLines?: boolean;
  debug?: boolean;
  disableResponseWordByWordLoading?: boolean;
  state?: "streaming" | "done";
  isLast?: boolean;
}

const markdownTokenRegex = /(`[^`]+`|\[.*?]\(.*?\)|\*\*.*?\*\*|\*[^*]+\*)/g;

const markdownConstructRegex =
  /^`[^`]+`$|^\[.*?]\(.*?\)$|^\*\*.*?\*\*$|^\*[^*]+\*$/;

const whitespaceRegex = /(\s+)/;

const REVEAL_TICK_MS = 50;
/**
 * The reveal is theatre over text that has already arrived, so it is bounded
 * rather than paced per word. One chunk per tick meant a long proposal — an
 * Agentic Logic rule plan runs well past a thousand words — took over a minute
 * to paint, entirely client-side, while its approval chips (which read
 * `nextAction`, not this animation) were already live. A merchant could approve
 * a proposal they had not been shown.
 *
 * Short replies still reveal word by word; only long ones speed up, so the
 * whole reveal lands inside the budget regardless of length.
 */
const REVEAL_BUDGET_MS = 2500;

const chunksPerTick = (totalChunks: number): number =>
  Math.max(1, Math.ceil(totalChunks / (REVEAL_BUDGET_MS / REVEAL_TICK_MS)));

export const Text = ({
  text,
  state,
  isLast = false,
  disableResponseWordByWordLoading,
}: TextProps) => {
  const initialStateRef = useRef(state);
  const revealId = useId();
  const [displayedText, setDisplayedText] = useState("");
  const displayedChunksRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Split text into chunks: words for prose, atomic units for markdown constructs
  const splitIntoChunks = (text: string): string[] => {
    const segments = text.split(/(\n)/);
    const chunks: string[] = [];
    let inCodeBlock = false;

    for (const line of segments) {
      if (line === "\n") {
        chunks.push(line);
        continue;
      }

      if (line.trim().startsWith("```")) {
        inCodeBlock = !inCodeBlock;
        chunks.push(line);
        continue;
      }

      // Lines inside code blocks are atomic
      if (inCodeBlock) {
        chunks.push(line);
        continue;
      }

      // Table rows are atomic
      if (line.includes("|")) {
        chunks.push(line);
        continue;
      }

      // Split line by markdown tokens, preserving constructs
      // Handles: `code`, [text](url), **bold**, *italic*
      const tokens = line.split(markdownTokenRegex);

      for (const token of tokens) {
        if (!token) {
          continue;
        }

        // Check if token is markdown construct
        if (markdownConstructRegex.test(token)) {
          chunks.push(token); // Keep whole
        } else {
          // Regular text - split by words, preserving whitespace
          const words = token.split(whitespaceRegex);
          chunks.push(...words.filter((w) => w.length > 0));
        }
      }
    }

    return chunks.filter((c) => c.length > 0);
  };

  useEffect(() => {
    if (
      disableResponseWordByWordLoading ||
      state === undefined ||
      initialStateRef.current === "done" ||
      !isLast
    ) {
      setDisplayedText(text);
      displayedChunksRef.current = splitIntoChunks(text).length;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [disableResponseWordByWordLoading, state, text, isLast]);

  useEffect(() => {
    if (disableResponseWordByWordLoading) {
      return;
    }

    if (
      isLast &&
      initialStateRef.current !== "done" &&
      (state === "streaming" || state === "done")
    ) {
      // Start interval if not already running
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          const currentChunks = splitIntoChunks(text);

          if (displayedChunksRef.current < currentChunks.length) {
            displayedChunksRef.current = Math.min(
              currentChunks.length,
              displayedChunksRef.current + chunksPerTick(currentChunks.length)
            );
            setDisplayedText(
              currentChunks.slice(0, displayedChunksRef.current).join("")
            );
          } else if (state === "done") {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        }, REVEAL_TICK_MS);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [disableResponseWordByWordLoading, state, text]);

  // Tells the composer's suggestion strip to hold its chips until this bubble
  // has finished painting — see `TextRevealContext`.
  useReportTextReveal(revealId, displayedText.length < text.length);

  if (text.toLowerCase().includes("noop")) {
    return null;
  }

  return (
    <Grid>
      <Grid>
        <Response
          mode={
            disableResponseWordByWordLoading || !isLast ? "static" : "streaming"
          }
        >
          {displayedText}
        </Response>
      </Grid>
    </Grid>
  );
};
